import api, { UPLOAD_REQUEST_TIMEOUT_MS } from "./api";
import {
  AppStatus,
  ApplicationNotesDict,
  Borrower,
  BorrowerDetail,
  CreateBorrowerData,
  UpdateBorrowerData,
  PaginatedResponse,
  AppStatusChoice,
  AppStatusTransitions,
  BorrowerFilters,
  DealChecklistRow,
  MerchantBankAccount,
  MerchantBankAccountInput,
} from "@/types/borrower";
import type {
  AnalysisOverridePayload,
  AnalysisOverrideTarget,
  CorrectionItem,
  CorrectionPreview,
} from "@/types/analysisRun";
import type { AnalysisOutput } from "@/types/underwriting";
import type {
  BaselayerAssociatedPull,
  BaselayerCheck,
  BaselayerRunRequest,
  BaselayerRunResponse,
  BaselayerSection,
  WebPresenceEntry,
  WebPresenceOrder,
} from "@/types/baselayer";

/** A Baselayer search can poll for a while; the saved state is polled separately either way. */
const BASELAYER_PULL_TIMEOUT_MS = 300_000;

/** Multi-value list filters go out as repeated params (`state=NY&state=NJ`), never `state[]=`. */
const REPEATED_PARAMS = { indexes: null };

export const borrowerService = {
  list: async (
    params: BorrowerFilters & { page?: number; page_size?: number } = {}
  ): Promise<PaginatedResponse<Borrower>> => {
    const response = await api.get<PaginatedResponse<Borrower>>("/borrowers/", {
      params,
      paramsSerializer: REPEATED_PARAMS,
    });
    return response.data;
  },

  /**
   * Workflow board variant (`?view=workflow`). The backend already scopes this
   * to a small, pre-filtered set of statuses/date range and returns it as a
   * single unpaginated array, so this hits the endpoint exactly once instead of
   * fanning out requests across every page.
   */
  listWorkflow: async (params: BorrowerFilters): Promise<Borrower[]> => {
    const response = await api.get<Borrower[]>("/borrowers/", {
      params,
      paramsSerializer: REPEATED_PARAMS,
    });
    return response.data;
  },

  getById: async (id: number): Promise<BorrowerDetail> => {
    const response = await api.get<BorrowerDetail>(`/borrowers/${id}/`);
    return response.data;
  },

  create: async (data: CreateBorrowerData): Promise<Borrower> => {
    const response = await api.post<Borrower>("/borrowers/", data);
    return response.data;
  },

  update: async (id: number, data: Partial<CreateBorrowerData>): Promise<Borrower> => {
    const response = await api.put<Borrower>(`/borrowers/${id}/`, data);
    return response.data;
  },

  patch: async (id: number, data: UpdateBorrowerData): Promise<Borrower> => {
    const response = await api.patch<Borrower>(`/borrowers/${id}/`, data);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/borrowers/${id}/`);
  },

  uploadStatements: async (
    borrowerId: number,
    files: File[]
  ): Promise<{ message: string; statement_ids: number[] }> => {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append("files", file);
    });

    const response = await api.post<{ message: string; statement_ids: number[] }>(
      `/borrowers/${borrowerId}/upload-statements/`,
      formData,
      {
        timeout: UPLOAD_REQUEST_TIMEOUT_MS,
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return response.data;
  },

  /**
   * Manually re-run the unified intake pipeline (after a field edit or a
   * document upload). Returns `{ action: "needs_documents", issues }` when the
   * blocking issues are document-related and nothing new was uploaded, or
   * `{ action: "reprocessing", intake_status: "processing" }` (202) otherwise.
   */
  reprocessIntake: async (
    borrowerId: number
  ): Promise<{ action: string; intake_status?: string; issues?: unknown[] }> => {
    const response = await api.post<{
      action: string;
      intake_status?: string;
      issues?: unknown[];
    }>(`/borrowers/${borrowerId}/reprocess-intake/`);
    return response.data;
  },

  /**
   * Manually correct a bank statement's detected month/year (fixes an AI
   * mis-extraction without a re-upload), then re-trigger intake evaluation.
   * Only accepted while the application is `needs_input`.
   */
  updateStatementPeriod: async (
    borrowerId: number,
    fileId: number,
    month: string,
    year: string
  ): Promise<{ message: string; intake_status: string }> => {
    const response = await api.patch<{ message: string; intake_status: string }>(
      `/borrowers/${borrowerId}/documents/${fileId}/statement-period/`,
      { month, year }
    );
    return response.data;
  },

  /**
   * Batch-set document types (and optional identity fields). Does not re-run
   * AI classification; re-evaluates intake readiness or incrementally syncs
   * bank-statement membership / underwriting as needed.
   */
  updateDocumentTypes: async (
    borrowerId: number,
    payload: {
      updates: { file_id: number; doc_type: string }[];
      company_name?: string;
      owner_name?: string;
      ein?: string;
    }
  ): Promise<{
    message: string;
    intake_status?: string;
    changed?: number;
    fields_changed?: boolean;
    statement_ids?: number[];
  }> => {
    const response = await api.patch<{
      message: string;
      intake_status?: string;
      changed?: number;
      fields_changed?: boolean;
      statement_ids?: number[];
    }>(`/borrowers/${borrowerId}/documents/doc-types/`, payload);
    return response.data;
  },

  triggerUnderwriting: async (
    borrowerId: number
  ): Promise<{ message: string; decision_id: number }> => {
    const response = await api.post<{ message: string; decision_id: number }>(
      `/borrowers/${borrowerId}/process-underwriting/`
    );
    return response.data;
  },

  /** Re-run statement AI analysis for the borrower. */
  rerunAnalysis: async (
    borrowerId: number,
    files?: File[],
    useOcr = false
  ): Promise<{ message: string; job_id: number | null; statement_ids: number[] }> => {
    const formData = new FormData();

    if (files && files.length > 0) {
      files.forEach((file) => {
        formData.append("files", file);
      });
    }
    formData.append("use_ocr", useOcr.toString());

    const response = await api.post<{
      message: string;
      job_id: number | null;
      statement_ids: number[];
    }>(`/borrowers/${borrowerId}/rerun-analysis/`, formData, {
      timeout: UPLOAD_REQUEST_TIMEOUT_MS,
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  },

  /**
   * Trigger batch refresh of all applications using cached data.
   * Processes sequentially with live progress via WebSocket.
   * Temporary feature for testing - will be removed later.
   */
  refreshAllAnalysis: async (): Promise<{ message: string; total: number }> => {
    const response = await api.post<{ message: string; total: number }>(
      "/borrowers/refresh-all-analysis/"
    );
    return response.data;
  },

  /** Re-run UniCourt search and reconcile cases (non-destructive; use ?reset=true on the API to wipe rows first). */
  refreshUnicourtCases: async (borrowerId: number): Promise<void> => {
    await api.post(`/borrowers/${borrowerId}/refresh-unicourt-cases/`);
  },

  /** Trigger DataMerch notes refresh (Default History tab). */
  refreshDatamerch: async (borrowerId: number): Promise<void> => {
    await api.post(`/borrowers/${borrowerId}/refresh-datamerch/`);
  },

  /** Refresh a single UniCourt case by id. */
  refreshUnicourtCase: async (borrowerId: number, caseId: string): Promise<void> => {
    await api.post(`/borrowers/${borrowerId}/refresh-unicourt-case/${caseId}/`);
  },
  claim: async (
    id: number
  ): Promise<{ underwriter_email: string | null; underwriter_name: string | null }> => {
    const response = await api.post<{
      underwriter_email: string | null;
      underwriter_name: string | null;
    }>(`/borrowers/${id}/claim/`);
    return response.data;
  },

  resetUnderwriter: async (
    id: number
  ): Promise<{ underwriter_email: null; underwriter_name: null }> => {
    const response = await api.post<{ underwriter_email: null; underwriter_name: null }>(
      `/borrowers/${id}/reset-underwriter/`
    );
    return response.data;
  },

  assignUnderwriter: async (
    borrowerId: number,
    underwriterId: number
  ): Promise<{ underwriter_email: string; underwriter_name: string }> => {
    const response = await api.post<{ underwriter_email: string; underwriter_name: string }>(
      `/borrowers/${borrowerId}/assign-underwriter/`,
      { underwriter_id: underwriterId }
    );
    return response.data;
  },
  getAppStatusTransitions: async (): Promise<{
    choices: AppStatusChoice[];
    transitions: AppStatusTransitions;
    manual_patch_blocked: AppStatus[];
  }> => {
    const response = await api.get<{
      choices: AppStatusChoice[];
      transitions: AppStatusTransitions;
      manual_patch_blocked: AppStatus[];
    }>("/borrowers/app-status-transitions/");
    return response.data;
  },

  /** Deal-scoped merchant checklist rows. */
  getChecklist: async (borrowerId: number): Promise<DealChecklistRow[]> => {
    const response = await api.get<{ rows: DealChecklistRow[] }>(
      `/borrowers/${borrowerId}/checklist/`
    );
    return response.data.rows;
  },

  plaidResendLink: async (
    borrowerId: number
  ): Promise<{
    resent: boolean;
    new_link_created: boolean;
    connect_url: string;
    expires_at: string;
  }> => {
    const response = await api.post<{
      resent: boolean;
      new_link_created: boolean;
      connect_url: string;
      expires_at: string;
    }>(`/borrowers/${borrowerId}/plaid-resend-link/`);
    return response.data;
  },

  /** Always creates a new Plaid asset report from active connected banks. */
  plaidRetryAssetReport: async (
    borrowerId: number
  ): Promise<{
    queued: boolean;
    action: "created";
    asset_report_id: string;
    institution_count: number;
  }> => {
    const response = await api.post<{
      queued: boolean;
      action: "created";
      asset_report_id: string;
      institution_count: number;
    }>(`/borrowers/${borrowerId}/plaid-retry-asset-report/`, {});
    return response.data;
  },

  plaidRetryPdf: async (borrowerId: number): Promise<{ queued: boolean }> => {
    const response = await api.post<{ queued: boolean }>(
      `/borrowers/${borrowerId}/plaid-retry-pdf/`,
      {}
    );
    return response.data;
  },

  /**
   * Re-run the Plaid asset-report AI analysis. `forceRetry` bypasses the
   * "already analyzed this report" skip so a re-run actually happens.
   */
  plaidRetryAnalysis: async (
    borrowerId: number,
    forceRetry = true
  ): Promise<{ queued: boolean }> => {
    const response = await api.post<{ queued: boolean }>(
      `/borrowers/${borrowerId}/plaid-retry-analysis/`,
      { force_retry: forceRetry }
    );
    return response.data;
  },

  /**
   * Escape hatch for a stuck/failed Plaid flow: move the deal to
   * final_underwriting off the statements (AI Analysis) decision instead.
   */
  forceFinalUnderwriting: async (borrowerId: number): Promise<BorrowerDetail> => {
    const response = await api.post<BorrowerDetail>(
      `/borrowers/${borrowerId}/force-final-underwriting/`,
      {}
    );
    return response.data;
  },

  /** Queue /auth/get for connected banks that have not yet returned ACH numbers. */
  plaidFetchAuth: async (
    borrowerId: number
  ): Promise<{ queued: boolean; item_count: number; item_ids: number[] }> => {
    const response = await api.post<{ queued: boolean; item_count: number; item_ids: number[] }>(
      `/borrowers/${borrowerId}/plaid-fetch-auth/`,
      {}
    );
    return response.data;
  },

  // ── Merchant bank accounts (multi-account; primary drives contract prefill) ──

  listBankAccounts: async (borrowerId: number): Promise<MerchantBankAccount[]> => {
    const response = await api.get<MerchantBankAccount[]>(
      `/borrowers/${borrowerId}/bank-accounts/`
    );
    return response.data;
  },

  createBankAccount: async (
    borrowerId: number,
    data: MerchantBankAccountInput
  ): Promise<MerchantBankAccount> => {
    const response = await api.post<MerchantBankAccount>(
      `/borrowers/${borrowerId}/bank-accounts/`,
      data
    );
    return response.data;
  },

  updateBankAccount: async (
    borrowerId: number,
    accountId: number,
    data: MerchantBankAccountInput
  ): Promise<MerchantBankAccount> => {
    const response = await api.patch<MerchantBankAccount>(
      `/borrowers/${borrowerId}/bank-accounts/${accountId}/`,
      data
    );
    return response.data;
  },

  deleteBankAccount: async (borrowerId: number, accountId: number): Promise<void> => {
    await api.delete(`/borrowers/${borrowerId}/bank-accounts/${accountId}/`);
  },

  /** Make the account primary; returns the borrower's full updated account list. */
  setPrimaryBankAccount: async (
    borrowerId: number,
    accountId: number
  ): Promise<MerchantBankAccount[]> => {
    const response = await api.post<MerchantBankAccount[]>(
      `/borrowers/${borrowerId}/bank-accounts/${accountId}/set-primary/`
    );
    return response.data;
  },

  addNote: async (
    borrowerId: number,
    text: string
  ): Promise<{ application_notes: ApplicationNotesDict }> => {
    const response = await api.post<{ application_notes: ApplicationNotesDict }>(
      `/borrowers/${borrowerId}/add-note/`,
      { text }
    );
    return response.data;
  },

  removeNote: async (
    borrowerId: number,
    noteId: string
  ): Promise<{ application_notes: ApplicationNotesDict }> => {
    const response = await api.delete<{ application_notes: ApplicationNotesDict }>(
      `/borrowers/${borrowerId}/remove-note/`,
      { data: { note_id: noteId } }
    );
    return response.data;
  },

  /**
   * Persist a position correction (active/inactive or frequency).
   * Server recomputes statements underwriting synchronously and returns the
   * corrected analysis payload.
   */
  saveAnalysisOverride: async (
    borrowerId: number,
    payload: AnalysisOverridePayload
  ): Promise<{ decision_id: number; analysis: AnalysisOutput | null }> => {
    const response = await api.post<{
      decision_id: number;
      analysis: AnalysisOutput | null;
    }>(`/borrowers/${borrowerId}/analysis-overrides/`, payload);
    return response.data;
  },

  /**
   * What corrections would change on the lane's latest finished analysis, worked out in memory:
   * nothing is stored.
   */
  previewAnalysisCorrections: async (
    borrowerId: number,
    items: CorrectionItem[],
    source: "statements" | "plaid" = "statements"
  ): Promise<CorrectionPreview> => {
    const response = await api.post<CorrectionPreview>(
      `/borrowers/${borrowerId}/analysis-corrections/preview/`,
      { items, source }
    );
    return response.data;
  },

  getBaselayerCheck: async (borrowerId: number): Promise<BaselayerCheck | null> => {
    const response = await api.get<{ check: BaselayerCheck | null }>(
      `/borrowers/${borrowerId}/baselayer-check/`
    );
    return response.data.check;
  },

  runBaselayerCheck: async (
    borrowerId: number,
    payload: BaselayerRunRequest
  ): Promise<BaselayerRunResponse> => {
    const response = await api.post<BaselayerRunResponse>(
      `/borrowers/${borrowerId}/baselayer-check/`,
      payload
    );
    return response.data;
  },

  /** Runs one queued (or failed) pull of a run; repeats return the saved state. */
  runBaselayerSection: async (
    borrowerId: number,
    checkId: number,
    section: BaselayerSection
  ): Promise<BaselayerCheck> => {
    const response = await api.post<{ check: BaselayerCheck }>(
      `/borrowers/${borrowerId}/baselayer-check/${checkId}/run/${section}/`,
      undefined,
      { timeout: BASELAYER_PULL_TIMEOUT_MS }
    );
    return response.data.check;
  },

  runBaselayerAssociatedPull: async (
    borrowerId: number,
    checkId: number,
    key: string
  ): Promise<BaselayerAssociatedPull> => {
    const response = await api.post<{ pull: BaselayerAssociatedPull }>(
      `/borrowers/${borrowerId}/baselayer-check/${checkId}/associated/`,
      { key },
      { timeout: BASELAYER_PULL_TIMEOUT_MS }
    );
    return response.data.pull;
  },

  getBaselayerWebPresence: async (borrowerId: number): Promise<WebPresenceEntry[]> => {
    const response = await api.get<{ web_presence: WebPresenceEntry[] }>(
      `/borrowers/${borrowerId}/baselayer-web-presence/`
    );
    return response.data.web_presence;
  },

  /** Re-reads one Order's search Baselayer is still running; never buys one. */
  refreshBaselayerWebPresence: async (
    borrowerId: number,
    order: WebPresenceOrder
  ): Promise<WebPresenceEntry[]> => {
    const response = await api.post<{ web_presence: WebPresenceEntry[] }>(
      `/borrowers/${borrowerId}/baselayer-web-presence/${order}/refresh/`,
      undefined,
      { timeout: BASELAYER_PULL_TIMEOUT_MS }
    );
    return response.data.web_presence;
  },

  clearAnalysisOverride: async (
    borrowerId: number,
    target: AnalysisOverrideTarget
  ): Promise<{ decision_id: number; analysis: AnalysisOutput | null }> => {
    const response = await api.delete<{
      decision_id: number;
      analysis: AnalysisOutput | null;
    }>(`/borrowers/${borrowerId}/analysis-overrides/`, { data: target });
    return response.data;
  },
};
